import { useQueryClient } from '@tanstack/react-query';
import { asyncResult } from '@vertex-protocol/utils';
import { usePrimaryChainVertexClient } from '@vertex-protocol/react-client';
import { useSubaccountContext } from 'client/context/subaccount/SubaccountContext';
import {
  SubaccountOpenEngineOrders,
  subaccountOpenEngineOrdersQueryKey,
} from 'client/hooks/query/subaccount/useSubaccountOpenEngineOrders';
import { useCallback } from 'react';

interface OpenOrdersForProductsResponse {
  productOrders: {
    productId: number;
    orders: SubaccountOpenEngineOrders[number];
  }[];
}

export function useRefetchOpenEngineOrders() {
  const vertexClient = usePrimaryChainVertexClient();
  const queryClient = useQueryClient();
  const {
    currentSubaccount: {
      name: subaccountName,
      address: subaccountOwner,
      chainEnv,
    },
  } = useSubaccountContext();

  return useCallback(
    async (productIds: number[]) => {
      if (!subaccountOwner || !vertexClient || !productIds.length) {
        return;
      }

      const refetchFn = async () => {
        const [newOrdersForProducts, error] = await asyncResult(
          vertexClient.market.getOpenSubaccountMultiProductOrders({
            productIds,
            subaccountName,
            subaccountOwner,
          }),
        );
        const openOrdersForProducts =
          newOrdersForProducts as OpenOrdersForProductsResponse | undefined;

        const queryKey = subaccountOpenEngineOrdersQueryKey(
          chainEnv,
          subaccountOwner,
          subaccountName,
        );

        if (!openOrdersForProducts || error) {
          // We couldn't load up-to-date data, so mark queries as stale (we don't want to refetch immediately as its unlikely
          // that a second query will succeed if the first fails
          queryClient.invalidateQueries({
            queryKey,
          });
          return;
        }

        queryClient.setQueriesData<SubaccountOpenEngineOrders>(
          {
            queryKey,
          },
          (prevData) => {
            const newData = {
              ...prevData,
            };

            openOrdersForProducts.productOrders.forEach((ordersForProduct) => {
              newData[ordersForProduct.productId] = ordersForProduct.orders;
            });

            return newData;
          },
        );
      };

      setTimeout(refetchFn, 50);
    },
    [chainEnv, queryClient, subaccountName, subaccountOwner, vertexClient],
  );
}
